import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { CalendarDays, CreditCard, List, Package } from 'lucide-react';

import {
  Content,
  ListContent,
  FormScrollArea,
  FormMainContainer,
} from './style';

import { FinancialEntriesOutletContext, FinancialEntryListFilters } from './types';
import {
  FINANCIAL_ENTRY_SORT_BY,
  FINANCIAL_VIEW_BY,
  FinancialViewByValue,
  getDefaultSortForView,
  getSortOptionsForView,
} from './constants';
import { financialEntryApi } from './api';
import { registerPaymentMethodCatalog } from '../../../utils/paymentMethodLabels';
import { getFinancialFilterHeaders } from './financialFilterConfig';
import { ViewModeSelector } from '../Pipelines/PipelineControls';

import { TypeMainOutlet } from '..';

import {
  FinancialFormActionsProvider,
  useFinancialFormActions,
} from './FinancialFormActionsContext';

import { CancelButton, SaveButton } from './FinancialEntryForm/style';
import { PageHeader } from '../../../components/StandardPage';
import { Filter } from '../../../components/SubHeaderFilter/types';
import {
  financialEntryRoutes,
  isFinancialEntryEditPath,
  isFinancialEntryFormPath,
} from './routes';
import {
  FINANCIAL_TAB_ENTRY,
  financialSectionTabs,
} from '../financialSectionTabs';

import FinancialCalendar from './FinancialCalendar';
import SelectEntryTypeModal from './SelectEntryTypeModal';
import {
  CALENDAR_SCOPE_OPTIONS,
  CalendarScope,
  FINANCIAL_CALENDAR_SCOPE,
} from './FinancialCalendar/calendarUtils';
import { SegmentButton, Segmented } from './FinancialCalendar/style';

type FinancialDisplayMode = 'list' | 'calendar';

const FinancialEntriesLayoutInner: React.FC = () => {
  const location = useLocation();
  const parentContext = useOutletContext<TypeMainOutlet>();
  const navigate = useNavigate();
  const { triggerSave, saving } = useFinancialFormActions();

  const search = parentContext?.search ?? '';

  const [filters, setFilters] = useState<FinancialEntryListFilters>({
    sortBy: FINANCIAL_ENTRY_SORT_BY.CREATED_AT,
    viewBy: FINANCIAL_VIEW_BY.ENTRY,
    appliedFilters: [],
  });

  const [refreshKey, setRefreshKey] = useState(0);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);

  // Lista (tabela) ou calendário. No calendário, o período (semana/mês/ano)
  // define a janela de vencimentos buscada, incluindo recorrências previstas.
  const [displayMode, setDisplayMode] = useState<FinancialDisplayMode>('list');
  const [calendarScope, setCalendarScope] = useState<CalendarScope>(
    FINANCIAL_CALENDAR_SCOPE.MONTH,
  );

  useEffect(() => {
    financialEntryApi
      .getPaymentMethods()
      .then(registerPaymentMethodCatalog)
      .catch(() => undefined);
  }, []);

  const isFormPage = isFinancialEntryFormPath(location.pathname);
  const isEditPage = isFinancialEntryEditPath(location.pathname);

  useEffect(() => {
    if (!isEditPage) return;
    const params = new URLSearchParams(location.search);
    if (params.get('readOnly') === 'true' || params.get('mode') === 'view') {
      navigate(financialEntryRoutes.list, { replace: true });
    }
  }, [isEditPage, location.search, navigate]);

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const outletContext: FinancialEntriesOutletContext = {
    search,
    filters,
    setFilters,
    refreshKey,
    triggerRefresh,
  };

  const handleFiltersChange = (patch: Partial<FinancialEntryListFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const appliedFilters = filters.appliedFilters ?? [];
  const viewBy = filters.viewBy ?? FINANCIAL_VIEW_BY.ENTRY;
  const isCalendar = displayMode === 'calendar';
  // O calendário sempre trabalha sobre lançamentos; os filtros seguem esse conjunto.
  const filterHeaders = getFinancialFilterHeaders(
    isCalendar ? FINANCIAL_VIEW_BY.ENTRY : viewBy,
  );

  const handleApplyFilter = (filter: Filter) => {
    handleFiltersChange({ appliedFilters: [...appliedFilters, filter] });
  };

  const handleRemoveFilter = (index: number) => {
    handleFiltersChange({
      appliedFilters: appliedFilters.filter((_, i) => i !== index),
    });
  };

  const handleClearAllFilters = () => {
    handleFiltersChange({ appliedFilters: [] });
  };

  const handleDisplayModeChange = (mode: FinancialDisplayMode) => {
    if (mode === displayMode) return;
    setDisplayMode(mode);
    // Filtros de "Pagamentos"/"Itens" não se aplicam ao calendário de lançamentos.
    if (mode === 'calendar' && viewBy !== FINANCIAL_VIEW_BY.ENTRY) {
      handleFiltersChange({ appliedFilters: [] });
    }
  };

  const displayModeSelector = (
    <Segmented role="group" aria-label="Modo de exibição">
      <SegmentButton
        type="button"
        $active={!isCalendar}
        onClick={() => handleDisplayModeChange('list')}
      >
        <List size={14} />
        Lista
      </SegmentButton>
      <SegmentButton
        type="button"
        $active={isCalendar}
        onClick={() => handleDisplayModeChange('calendar')}
      >
        <CalendarDays size={14} />
        Calendário
      </SegmentButton>
    </Segmented>
  );

  const calendarScopeSelector = (
    <Segmented role="group" aria-label="Período do calendário">
      {CALENDAR_SCOPE_OPTIONS.map((option) => (
        <SegmentButton
          key={option.value}
          type="button"
          $active={calendarScope === option.value}
          onClick={() => setCalendarScope(option.value)}
        >
          {option.label}
        </SegmentButton>
      ))}
    </Segmented>
  );

  const listViewSelector = (
    <ViewModeSelector
      value={viewBy}
      options={[
        { value: FINANCIAL_VIEW_BY.ENTRY, label: 'Lançamentos', icon: <List size={16} /> },
        { value: FINANCIAL_VIEW_BY.PAYMENT, label: 'Pagamentos', icon: <CreditCard size={16} /> },
        { value: FINANCIAL_VIEW_BY.ITEM, label: 'Itens', icon: <Package size={16} /> },
      ]}
      onChange={(nextViewBy: FinancialViewByValue) => {
        const nextSortOptions = getSortOptionsForView(nextViewBy);
        const currentSort = filters.sortBy ?? getDefaultSortForView(viewBy);
        const sortBy = nextSortOptions.some((option) => option.value === currentSort)
          ? currentSort
          : getDefaultSortForView(nextViewBy);

        handleFiltersChange({
          viewBy: nextViewBy,
          sortBy,
          appliedFilters: [],
        });
      }}
    />
  );

  return (
    <>
      {isFormPage ? (
        <PageHeader
          title={isEditPage ? 'Editar lançamento' : 'Novo lançamento'}
          hasTable={false}
          showAuditHeaderMenu={false}
          extraActions={
            <>
              <CancelButton type="button" onClick={() => navigate(financialEntryRoutes.list)}>
                Cancelar
              </CancelButton>
              <SaveButton type="button" disabled={saving} onClick={triggerSave}>
                {saving ? 'Salvando...' : isEditPage ? 'Salvar alterações' : 'Criar lançamento'}
              </SaveButton>
            </>
          }
        />
      ) : (
        <PageHeader
          title="Financeiro"
          tableName="FinancialEntry"
          moduleName="Financeiro"
          tabs={financialSectionTabs.map(({ key, label, icon }) => ({
            key,
            label,
            icon,
          }))}
          activeTab={FINANCIAL_TAB_ENTRY}
          onTabChange={(key) => {
            const tab = financialSectionTabs.find((item) => item.key === key);
            if (tab) navigate(tab.path);
          }}
          onReload={triggerRefresh}
          rightExtra={
            <>
              {isCalendar && calendarScopeSelector}
              {displayModeSelector}
              {!isCalendar && listViewSelector}
            </>
          }
          filterHeaders={filterHeaders}
          appliedFilters={appliedFilters}
          onApplyFilter={handleApplyFilter}
          onRemoveFilter={handleRemoveFilter}
          onClearAllFilters={handleClearAllFilters}
          createButton={{
            label: 'Novo lançamento',
            onClick: () => setIsTypeModalOpen(true),
          }}
        />
      )}

      {isTypeModalOpen && (
        <SelectEntryTypeModal
          onClose={() => setIsTypeModalOpen(false)}
          onSelect={(type) => {
            setIsTypeModalOpen(false);
            navigate(financialEntryRoutes.newWithType(type));
          }}
        />
      )}

      {isFormPage ? (
        <Content>
          <FormScrollArea>
            <FormMainContainer>
              <Outlet context={outletContext} />
            </FormMainContainer>
          </FormScrollArea>
        </Content>
      ) : (
        <ListContent>
          {isCalendar ? (
            <FinancialCalendar
              scope={calendarScope}
              onScopeChange={setCalendarScope}
              search={search}
              filters={filters}
              refreshKey={refreshKey}
            />
          ) : (
            <Outlet context={outletContext} />
          )}
        </ListContent>
      )}
    </>
  );
};

const FinancialEntriesLayout: React.FC = () => (
  <FinancialFormActionsProvider>
    <FinancialEntriesLayoutInner />
  </FinancialFormActionsProvider>
);

export default FinancialEntriesLayout;
